import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import axios from 'axios';
import './Profile.css';
import LoyaltyWallet from '../components/LoyaltyWallet';

const Profile = () => {
  const { currentUser, authToken, updateUser } = useAuth();
  const { walletData, getAllBalances, getFormattedBalanceForCurrency } = useWallet();
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    phone_number: '',
    country_code: '',
    date_of_birth: ''
  });
  const [saving, setSaving] = useState(false);

  // Preferred currency state
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [savingCurrency, setSavingCurrency] = useState(false);

  // Game history state
  const [gameHistory, setGameHistory] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gamesPage, setGamesPage] = useState(1);
  const [gamesPagination, setGamesPagination] = useState(null);

  // Active tab for profile sections
  const [activeTab, setActiveTab] = useState('overview');

  const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
  const headers = { Authorization: `Bearer ${authToken}` };

  // Fetch full profile on mount
  useEffect(() => {
    fetchProfile();
    fetchGameHistory(1);
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/users/profile`, { headers });
      setProfile(res.data);
      setEditForm({
        username: res.data.username || '',
        phone_number: res.data.phone_number || '',
        country_code: res.data.country_code || '',
        date_of_birth: res.data.date_of_birth ? res.data.date_of_birth.split('T')[0] : ''
      });
      setPreferredCurrency(res.data.preferred_currency || 'USD');
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGameHistory = async (page) => {
    try {
      setGamesLoading(true);
      const res = await axios.get(`${API_BASE}/api/users/game-history?page=${page}&limit=10`, { headers });
      setGameHistory(res.data.games || []);
      setGamesPagination(res.data.pagination);
      setGamesPage(page);
    } catch (err) {
      console.error('Error fetching game history:', err);
    } finally {
      setGamesLoading(false);
    }
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');
      const res = await axios.put(`${API_BASE}/api/users/profile`, editForm, { headers });
      setProfile(prev => ({ ...prev, ...res.data.user }));
      updateUser(res.data.user);
      setSuccessMsg('Profile updated successfully!');
      setEditMode(false);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = async (e) => {
    const newCurrency = e.target.value;
    try {
      setSavingCurrency(true);
      setError('');
      setSuccessMsg('');
      await axios.put(`${API_BASE}/api/users/preferred-currency`, { currency: newCurrency }, { headers });
      setPreferredCurrency(newCurrency);
      setSuccessMsg('Preferred currency updated!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Failed to update preferred currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getKycBadge = (status) => {
    if (!status || status === 'not_submitted') return <span className="kyc-badge kyc-pending">Not Submitted</span>;
    const map = {
      'verified': <span className="kyc-badge kyc-verified">✓ Verified</span>,
      'pending': <span className="kyc-badge kyc-pending">⏳ Pending</span>,
      'rejected': <span className="kyc-badge kyc-rejected">✗ Rejected</span>,
      'under_review': <span className="kyc-badge kyc-pending">Under Review</span>
    };
    return map[status] || <span className="kyc-badge kyc-pending">{status}</span>;
  };

  const getGameStatusBadge = (status) => {
    const map = {
      'completed': <span className="game-status completed">Completed</span>,
      'in-progress': <span className="game-status in-progress">In Progress</span>,
      'cancelled': <span className="game-status cancelled">Cancelled</span>
    };
    return map[status] || <span className="game-status">{status}</span>;
  };

  if (loading) {
    return <div className="profile-loading">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="profile-error">Failed to load profile. Please try again.</div>;
  }

  const allBalances = getAllBalances();

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar">
          {profile.username?.charAt(0).toUpperCase() || 'U'}
        </div>
        <div className="profile-title">
          <h1>{profile.username}</h1>
          <p className="profile-email">{profile.email}</p>
          <p className="profile-member-since">Member since {formatDate(profile.created_at)}</p>
        </div>
        {getKycBadge(profile.kyc_verified === true ? 'verified' : profile.kyc_status)}
      </div>

      {error && <div className="profile-error-message">{error}</div>}
      {successMsg && <div className="profile-success-message">{successMsg}</div>}

      <div className="profile-tabs">
        <button className={activeTab === 'overview' ? 'tab active' : 'tab'} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={activeTab === 'edit' ? 'tab active' : 'tab'} onClick={() => setActiveTab('edit')}>Edit Profile</button>
        <button className={activeTab === 'game-history' ? 'tab active' : 'tab'} onClick={() => setActiveTab('game-history')}>Game History</button>
      </div>

      {activeTab === 'overview' && (
        <div className="profile-overview">
          {/* Wallet Balances */}
          <div className="profile-section">
            <h3><span className="section-icon">💰</span> Wallet Balances</h3>
            <div className="balances-grid">
              {allBalances.map((b, i) => (
                <div key={i} className={`balance-card currency-${b.currency.toLowerCase()}`}>
                  <span className="balance-label">{b.currency}</span>
                  <span className="balance-value">{b.formatted}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bonuses & Loyalty */}
          <LoyaltyWallet authToken={authToken} />

          {/* Personal Information */}
          <div className="profile-section">
            <h3><span className="section-icon">👤</span> Personal Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Username</span>
                <span className="info-value">{profile.username}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{profile.email}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{profile.phone_number || 'Not set'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Country Code</span>
                <span className="info-value">{profile.country_code || 'Not set'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Date of Birth</span>
                <span className="info-value">{profile.date_of_birth ? formatDate(profile.date_of_birth) : 'Not set'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Preferred Currency</span>
                <span className="info-value">{preferredCurrency}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Login</span>
                <span className="info-value">{profile.last_login ? formatDate(profile.last_login) : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">KYC Status</span>
                <span className="info-value">{getKycBadge(profile.kyc_verified === true ? 'verified' : profile.kyc_status)}</span>
              </div>
            </div>
          </div>

          {/* Game Statistics */}
          <div className="profile-section">
            <h3><span className="section-icon">📊</span> Game Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-number">{profile.total_games || 0}</span>
                <span className="stat-desc">Games Played</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">{profile.total_winnings || 0}</span>
                <span className="stat-desc">Total Winnings</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">{profile.total_games > 0 ? ((profile.total_winnings / profile.total_games) * 100).toFixed(1) : 0}%</span>
                <span className="stat-desc">Win Rate</span>
              </div>
            </div>
          </div>

          {/* Preferred Currency Selector */}
          <div className="profile-section">
            <h3><span className="section-icon">🌐</span> Preferred Currency</h3>
            <div className="currency-selector">
              <select value={preferredCurrency} onChange={handleCurrencyChange} disabled={savingCurrency}>
                <option value="USD">USD - US Dollar</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="LRD">LRD - Liberian Dollar</option>
              </select>
              {savingCurrency && <span className="saving-indicator">Saving...</span>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'edit' && (
        <div className="profile-edit-section">
          <h3><span className="section-icon">✏️</span> Edit Profile</h3>
          <form onSubmit={handleSaveProfile} className="profile-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={editForm.username}
                onChange={handleEditChange}
                minLength={3}
                maxLength={20}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone_number">Phone Number</label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={editForm.phone_number}
                onChange={handleEditChange}
                placeholder="+1234567890"
              />
            </div>
            <div className="form-group">
              <label htmlFor="country_code">Country Code</label>
              <input
                type="text"
                id="country_code"
                name="country_code"
                value={editForm.country_code}
                onChange={handleEditChange}
                placeholder="US"
                maxLength={3}
              />
            </div>
            <div className="form-group">
              <label htmlFor="date_of_birth">Date of Birth</label>
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={editForm.date_of_birth}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-buttons">
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" className="btn-cancel" onClick={() => {
                setEditMode(false);
                setEditForm({
                  username: profile.username,
                  phone_number: profile.phone_number || '',
                  country_code: profile.country_code || '',
                  date_of_birth: profile.date_of_birth ? profile.date_of_birth.split('T')[0] : ''
                });
                setError('');
              }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'game-history' && (
        <div className="profile-games-section">
          <h3><span className="section-icon">🎮</span> Game History</h3>
          {gamesLoading && <div className="games-loading">Loading game history...</div>}
          {!gamesLoading && gameHistory.length === 0 && (
            <div className="no-games">No games played yet. Start playing to see your history!</div>
          )}
          {!gamesLoading && gameHistory.length > 0 && (
            <>
              <div className="games-table-wrapper">
                <table className="games-table">
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Score</th>
                      <th>Winnings</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gameHistory.map(game => (
                      <tr key={game.id}>
                        <td>{game.name || 'Game #' + game.id}</td>
                        <td>{game.type || game.game_type || '-'}</td>
                        <td>{getGameStatusBadge(game.status)}</td>
                        <td>{game.score || game.position || '-'}</td>
                        <td className={parseFloat(game.winnings || 0) > 0 ? 'winnings-positive' : ''}>
                          {game.winnings && parseFloat(game.winnings) > 0 ? `₹${game.winnings}` : '-'}
                        </td>
                        <td>{game.end_date || game.completed_at ? formatDate(game.end_date || game.completed_at) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {gamesPagination && (
                <div className="pagination">
                  <button 
                    disabled={!gamesPagination.hasPrev} 
                    onClick={() => fetchGameHistory(gamesPage - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="page-info">Page {gamesPagination.currentPage} of {gamesPagination.totalPages}</span>
                  <button 
                    disabled={!gamesPagination.hasNext} 
                    onClick={() => fetchGameHistory(gamesPage + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Profile;